import { useDispatch } from 'react-redux'
import { useSelector } from '@state/store'
import { useForm } from 'react-hook-form'

import { Grid } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'

import { SelectInput } from '@components/inputs'
import { TextInput } from '@components/inputs'
import {
  CreateLabWorkstationDto,
  LabWorkstation,
  RoomType,
  UpdateLabWorkstationDto,
} from '@services/api'
import { MouseEventHandler, useEffect, useMemo, useState } from 'react'
import {
  createLabWorkstation,
  updateLabWorkstation,
} from '../../../../state/thunks/roomThunk'
import { addLabWorkstationSchema } from '../../../../utils/schemas'
import { Button } from '@components/buttons'

type AddLabWorkstationFormProps = {
  row?: LabWorkstation
  onClose?: () => void
  onCancelButtonPress?: (
    ev: MouseEventHandler<HTMLButtonElement> | undefined,
  ) => void
}

export function LabWorkstationsForm({ row, onClose }: AddLabWorkstationFormProps) {
  const dispatch = useDispatch()
  const sites = useSelector(({ site }) => site.sites.datas)

  const [selectedSite, setSelectedSite] = useState<number | null>(null)

  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<LabWorkstation>({
    resolver: yupResolver(addLabWorkstationSchema),
    defaultValues: { ...row },
  })

  useEffect(() => {
    if (row?.site) {
      setSelectedSite(row.site.id)
    }
  }, [row])

  const onSubmit = (values: LabWorkstation & { type?: RoomType }) => {
    if (row?.id) {
      const body: UpdateLabWorkstationDto = {
        code: values.code,
        label: values.label,
        siteId: values.siteId,
      }
      dispatch(updateLabWorkstation({ id: row.id, body }))
    } else {
      //add room
      const body: CreateLabWorkstationDto = {
        code: values.code,
        label: values.label,
        siteId: values.siteId,
      }
      dispatch(createLabWorkstation(body))
    }

    if (onClose) onClose()
  }

  const handleCancelPress = () => {
    if (onClose) {
      onClose()
    }
    // Closes the update form - Datagrid didnt provide a prop function to close form ()
    const el = (document as Document).querySelector(
      `.MuiDataGrid-row[data-id="${row?.id}"] .MuiIconButton-root.MuiIconButton-sizeSmall`,
    ) as any
    if (el) {
      el.click()
    }
  }

  const sitesOptions = useMemo(
    () =>
      sites.map((_site) => ({
        label: _site.label,
        value: _site.id,
      })),
    [sites],
  )

  return (
    <Grid
      container
      spacing={1}
      justifyContent="center"
      alignItems="flex-start"
      sx={{
        padding: '20px',
      }}
    >
      <Grid item xs={12}>
        <SelectInput
          value={selectedSite}
          name="siteId"
          options={sitesOptions}
          control={control}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="code" errors={errors} control={control} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="label" errors={errors} control={control} />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2} justifyContent="flex-end">
          {onClose && (
            <Grid item xs={2}>
              <Button
                onClick={handleCancelPress}
                color="secondary"
                text="cancel"
                textColor="white"
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={2}>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="primary"
              text="save"
              textColor="white"
              fullWidth
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
